import React from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {AnimatePresence} from 'motion/react';

import {ChevronDown} from '../../../icons/ChevronDown';
import {Button} from '../../Button/Button';
import {OptionItem} from '../OptionItem/OptionItem';
import {OptionList} from '../OptionList/OptionList';

import {useFilterSelect} from './useFilterSelect';
import {getBaseVariables} from './utils';

import type {Option} from './useFilterSelect';

/**
 * Defines the properties for the `FilterSelectInput` component, which allows users to select from predefined options.
 * The component supports default values, disabling, and custom dropdown positioning. It also provides a callback to handle value
 * changes and can be targeted in tests using a `testId`.
 */
export interface ComponentProps {
    /**
     * The default value of the filter select. This value will be pre-selected when the component is rendered.
     * It can be any data type depending on the use case, but it should match one of the values in the `options` array.
     */
    defaultValue: any;
    /**
     * Optional. A boolean that indicates whether the filter select is disabled. When set to `true`,
     * the select input will be non-interactive and appear disabled in the UI.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * The callback function to handle value changes. It will be triggered whenever a user selects a new value.
     */
    onChange(value: any): void;
    /**
     * The available options for the filter select. Each option must be an object with a `label` and a `value`
     * that defines what is displayed and what is selected.
     */
    options: Option[];
    /**
     * Optional. The position of the popover list relative to the select input. This determines where the options
     * will appear when the input is clicked. It can either be on the left or the right.
     *
     * @default 'right'
     */
    popOverPosition?: 'left' | 'right';
    /**
     * Size variant that controls the overall dimensions and spacing of the button component.
     * The 'normal' size provides standard button dimensions suitable for most use cases, while 'small' creates a more compact version for space-constrained layouts.
     * This property affects padding, font size, and overall component dimensions to maintain proper visual hierarchy and usability.
     *
     * @default 'normal'
     */
    size?: 'large' | 'small';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'FilterSelectInput'
     */
    testId?: string;
}

/**
 * The `FilterSelectInput` component renders a customizable select input with options, which can be disabled and allows users to
 * choose an option from a list. The component also provides a popover list of options with animation and handles the user's selection
 * with the provided `onChange` callback.
 *
 * @param props                 The component props.
 * @param props.isKeepOnUnmount Optional. Determines whether to keep the form field value when the component unmounts. Defaults to `false`.
 * @param props.name            The unique name identifier for the select input within the form.
 * @param props.defaultValue    The default selected value for the select input.
 * @param props.isDisabled      Optional. Determines whether the select input is disabled. Defaults to `false`.
 * @param props.onChange        The callback function to handle changes when the user selects a new option.
 * @param props.options         An array of options to be rendered in the select input, each having a label and value.
 * @param props.popOverPosition Optional. The position of the popover list that displays the options. Defaults to `'right'`.
 * @param props.size            Optional. The size variant for the button component, which can be either 'large' or 'small'. Defaults to 'large'.
 * @param props.testId          Optional. A unique identifier for testing purposes. Defaults to `'FilterSelectInput'`.
 * @param props.validation      Optional validation function to validate the selected value.
 * @returns A JSX element representing the filter select input with animated popover options.
 *
 * @example
 * ```tsx
 * <FilterSelectInput
 *   defaultValue="Option1"
 *   onChange={handleSelectChange}
 *   options={[
 *     {label: 'Option1', value: '1'},
 *     {label: 'Option2', value: '2'}
 *   ]}
 *   popOverPosition="left"
 * />
 * ```
 */
const FilterSelectInput = ({
    defaultValue,
    isDisabled = false,
    onChange = () => null,
    options,
    popOverPosition = 'right',
    size = 'large',
    testId = 'FilterSelectInput'
}: ComponentProps) => {
    const {
        chosenOption,
        handleChangeValue,
        handleToggleSelect,
        isOpen,
        isTop,
        listHeight,
        selectRef
    } = useFilterSelect(defaultValue, options, onChange, isDisabled);

    return (
        <Wrapper
            ref={selectRef}
            $isOpen={isOpen}
            data-cy={testId}
            data-is-disabled={isDisabled}
            onClick={handleToggleSelect}
        >
            <Button
                as="button"
                icon={ChevronDown}
                iconClassName="chevron"
                iconPosition="end"
                size={size}
                testId={`${testId}-chosen-option`}
                variant="secondary"
            >
                {chosenOption}
            </Button>
            <AnimatePresence mode="wait">
                {isOpen && (
                    <OptionList
                        height={listHeight}
                        isOnTop={isTop}
                        position={popOverPosition}
                        testId={`${testId}-list`}
                    >
                        {options.map(({label, value}) => {
                            const isActive = chosenOption === label;

                            return (
                                <OptionItem
                                    key={value}
                                    index={String(value)}
                                    isActive={isActive}
                                    label={label}
                                    isSelectable
                                    onClick={handleChangeValue}
                                />
                            );
                        })}
                    </OptionList>
                )}
            </AnimatePresence>
        </Wrapper>

    );
};

FilterSelectInput.displayName = 'FilterSelectInput';

export {FilterSelectInput};

interface WrapperProps {
    $isOpen: boolean;
}

const Wrapper = styled.div<WrapperProps>`
    ${getBaseVariables}

    align-items: center;
    cursor: var(--cursor);
    display: flex;
    gap: ${spacing(2)};
    position: relative;
    width: fit-content;

    .chevron {
        transform: ${({$isOpen}) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
        transition: transform 0.3s ease-in-out;
    }
`;